'use client'

import { FunctionComponent, useEffect, useRef, useState } from 'react'
import {
  AccreditedNetworkList,
  Filters,
  MapContainer,
  FloatingActionButtons,
  SPECIALIST_DRAWER_ID,
  ESTABLISHMENT_DRAWER_ID
} from '@/modules/accredited-establishments/components'
import {
  useFilters,
  useInitFilters,
  useSealedSearchParams
} from '@/modules/accredited-establishments/hooks'
import { motion, AnimatePresence } from 'framer-motion'
// The library does not publish 'use client'; this file already declares it.
import { APIProvider } from '@vis.gl/react-google-maps'
import classNames from 'classnames'
import { EstablishmentCategory } from '@/modules/accredited-establishments/api/types'
import {
  useDispatchPageEvent,
  useDrawer,
  usePreventPageScroll
} from '@/global/hooks'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import { useSealedParamsContext } from '@/modules/accredited-establishments/contexts'
import {
  ESTABLISHMENT_PARAMS,
  SPECIALIST_PARAMS
} from '@/modules/accredited-establishments/constants'
import { EventName } from '@/global/utils/analytics-events/types/common'

const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY

const SpecialistDetailDrawer = dynamic(
  () =>
    import('@/modules/accredited-establishments/components').then(
      mod => mod.SpecialistDetailDrawer
    ),
  {
    ssr: false
  }
)

const EstablishmentDetailDrawer = dynamic(
  () =>
    import('@/modules/accredited-establishments/components').then(
      mod => mod.EstablishmentDetailDrawer
    ),
  { ssr: false }
)

const AccreditedEstablishmentsView: FunctionComponent = () => {
  useDispatchPageEvent({
    eventName: EventName.PAGE_VIEWED,
    pageTitle: 'Rede credenciada Listagem'
  })

  const { isMapView, isListView } = useFilters()

  // The Maps API loads only after the user opens the map (CDI-3087). The flag
  // never turns off, so the script loads once per page load.
  const [isMapRequested, setIsMapRequested] = useState(false)
  if (isMapView && !isMapRequested) setIsMapRequested(true)

  const pathname = usePathname()
  const { paramsRef } = useSealedParamsContext()
  const { updateSealedSearchParams } = useSealedSearchParams()

  const { open: openSpecialistDrawer, isOpen: isSpecialistDrawerOpen } =
    useDrawer(SPECIALIST_DRAWER_ID)
  const { open: openEstablishmentDrawer, isOpen: isEstablishmentDrawerOpen } =
    useDrawer(ESTABLISHMENT_DRAWER_ID)

  usePreventPageScroll(isSpecialistDrawerOpen || isEstablishmentDrawerOpen)

  useInitFilters()

  const [selectedEstablishment, setSelectedEstablishment] = useState<string>()
  const [selectedSpecialist, setSelectedSpecialist] = useState<string>()

  const hasOpenedDrawer = useRef({ specialist: false, establishment: false })

  /*
   * O prestador aberto vive na URL para que o link do botao compartilhar reabra
   * o drawer (HC-2613). `updateSealedSearchParams` escreve direto com
   * `history.replaceState` (sem navegacao, sem entrada nova no historico),
   * entao abrir e fechar o drawer nao empilha nada.
   */
  useEffect(() => {
    const sharedSpecialist = paramsRef.current[SPECIALIST_PARAMS]
    const sharedEstablishment = paramsRef.current[ESTABLISHMENT_PARAMS]

    /*
     * Um drawer por vez: uma URL com os dois parametros empilharia os dois
     * paineis. O especialista vem primeiro por ser o link mais compartilhado.
     */
    if (sharedSpecialist) {
      setSelectedSpecialist(sharedSpecialist)
      openSpecialistDrawer()

      return
    }

    if (sharedEstablishment) {
      setSelectedEstablishment(sharedEstablishment)
      openEstablishmentDrawer()
    }
    // Roda so na montagem: depois disso quem manda na URL e o proprio drawer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
   * So limpa depois que o drawer chegou a abrir. Sem essa trava a limpeza
   * rodaria na montagem e apagaria o parametro do link compartilhado antes de
   * o drawer abrir.
   */
  useEffect(() => {
    if (isSpecialistDrawerOpen) {
      hasOpenedDrawer.current.specialist = true
      return
    }

    if (!hasOpenedDrawer.current.specialist) return

    updateSealedSearchParams({ pathname, newParam: [[SPECIALIST_PARAMS, '']] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpecialistDrawerOpen, pathname])

  useEffect(() => {
    if (isEstablishmentDrawerOpen) {
      hasOpenedDrawer.current.establishment = true
      return
    }

    if (!hasOpenedDrawer.current.establishment) return

    updateSealedSearchParams({
      pathname,
      newParam: [[ESTABLISHMENT_PARAMS, '']]
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEstablishmentDrawerOpen, pathname])

  const onDetails = ({
    referencedId,
    category
  }: {
    referencedId: string
    category: EstablishmentCategory
  }) => {
    const param =
      category === 'SPECIALIST' ? SPECIALIST_PARAMS : ESTABLISHMENT_PARAMS

    updateSealedSearchParams({ pathname, newParam: [[param, referencedId]] })

    if (category === 'SPECIALIST') {
      setSelectedSpecialist(referencedId)
      openSpecialistDrawer()
    } else {
      setSelectedEstablishment(referencedId)
      openEstablishmentDrawer()
    }
  }

  return (
    <div
      data-testid='accredited-network-v2'
      className={classNames(
        'mt-[var(--app-header-height)] w-full flex',
        isListView
          ? 'min-h-[calc(100%-var(--app-header-height))]'
          : /* Viewport based: the height chain above this box is content driven. */
            'h-[calc(100dvh-var(--app-header-height))]'
      )}
    >
      <div
        className={classNames(
          'w-full flex flex-col px-gl-spacing-03 lg:px-16',
          !isListView && 'h-full'
        )}
      >
        <Filters />

        <FloatingActionButtons />

        <SpecialistDetailDrawer referencedId={selectedSpecialist} />

        <EstablishmentDetailDrawer referencedId={selectedEstablishment} />

        {/* The map takes what is left of the column after the filters bar. */}
        <div
          className={classNames(
            'relative',
            isMapView
              ? /*
                 * Full bleed on mobile: the handoff runs the map to both edges.
                 * No `w-full` here. The width has to stay `auto` so the flex
                 * stretch counts the negative margins; a fixed 100% would leave
                 * the padding uncovered on the right.
                 */
                'flex-1 min-h-0 overflow-y-hidden -mx-gl-spacing-03 lg:mx-0 lg:w-full'
              : /* The box stays mounted in list view, with no height and no clicks. */
                'w-full h-0 pointer-events-none'
          )}
        >
          <AnimatePresence>
            <motion.div
              key='map'
              data-testid='accredited-network-map'
              className='w-full h-full absolute'
              initial={{ opacity: 0, zIndex: 0 }}
              animate={{
                opacity: isMapView ? 1 : 0,
                zIndex: isMapView ? 1 : 0
              }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              {isMapRequested ? (
                <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                  <MapContainer onDetails={onDetails} />
                </APIProvider>
              ) : null}
            </motion.div>
          </AnimatePresence>
        </div>

        <motion.div
          key='list'
          data-testid='accredited-network-list'
          className={classNames(
            'w-full',
            /* In map view the list leaves the flow, or it would stretch the page. */
            isMapView && 'hidden'
          )}
          /* `initial={false}`: a re-render would restart the fade from zero. */
          initial={false}
          animate={{ opacity: isListView ? 1 : 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <AccreditedNetworkList onEstablishmentClick={onDetails} />
        </motion.div>
      </div>
    </div>
  )
}

export { AccreditedEstablishmentsView }
